// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */
import { graphql } from 'relay-runtime';
import { useState, useContext, useEffect } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { useFeedTypeSelectorWriteFragment$data } from './__generated__/useFeedTypeSelectorWriteFragment.graphql.ts';
import useFeedTypeSelectorWriteFragmentArtifact from './__generated__/useFeedTypeSelectorWriteFragment.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import type {
	useFeedTypeSelectorFeedTypeSelectorRefreshMutation as useFeedTypeSelectorFeedTypeSelectorRefreshMutation$data,
	useFeedTypeSelectorFeedTypeSelectorRefreshMutationVariables as useFeedTypeSelectorFeedTypeSelectorRefreshMutation$variables,
} from './__types__/useFeedTypeSelectorFeedTypeSelectorRefreshMutation.ts';
import type {
	useFeedTypeSelectorRefreshQuery$data,
	useFeedTypeSelectorRefreshQuery$variables,
} from './__generated__/useFeedTypeSelectorRefreshQuery.graphql.ts';
import { FeedType } from './feedTypeEnum.ts';

const useFeedTypeSelectorRefreshQuery = graphql`
	query useFeedTypeSelectorRefreshQuery {
		userPreferences {
			__typename
			feedTab
		}
	}
`;

const useFeedTypeSelectorFeedTypeSelectorRefreshMutation = graphql`
	mutation useFeedTypeSelectorFeedTypeSelectorRefreshMutation($feedTab: String!) {
		updateUserPreferences(userPreferences: { feedTab: $feedTab }) {
			feedTab
		}
	}
`;

type UseFeedTypeSelector = (defaultPreference?: FeedType) => {
	loading: boolean;
	queryError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
	feedType: FeedType | string;
	updateFeedType: (feedType: FeedType | string) => void;
	hasSelectedFeedTypePreference: boolean;
};

type UseFeedTypeOverride = () => {
	feedTypeOverride?: FeedType;
	clearFeedTypeOverride: () => void;
};

export const useFeedTypeSelector: (defaultPreference?: FeedType) => {
	feedType: FeedType;
	hasSelectedFeedTypePreference: boolean | StringConstructor;
	mutationError: any;
	queryError: any;
	loading: any;
	updateFeedType: (updatedFeedType: FeedType | string) => void;
} = (defaultPreference = FeedType.FOLLOWING) => {
	const { isLoggedIn } = useSessionData();
	const writeFragmentInterop = useWriteFragmentInterop<useFeedTypeSelectorWriteFragment$data>({
		fragmentArtifact: useFeedTypeSelectorWriteFragmentArtifact,
		fragmentSpec: graphql`
			fragment useFeedTypeSelectorWriteFragment on UserPreferences {
				feedTab
			}
		`,
	});

	const {
		data,
		loading,
		error: queryError,
	} = useQueryInterop<
		useFeedTypeSelectorRefreshQuery$data,
		useFeedTypeSelectorRefreshQuery$variables
	>(useFeedTypeSelectorRefreshQuery, {
		skip: !isLoggedIn,
	});

	const feedType = (data?.userPreferences?.feedTab as FeedType) || defaultPreference;
	const [feedTypeSelectorMutation, { error: mutationError }] = useMutationInterop<
		useFeedTypeSelectorFeedTypeSelectorRefreshMutation$data,
		useFeedTypeSelectorFeedTypeSelectorRefreshMutation$variables
	>(useFeedTypeSelectorFeedTypeSelectorRefreshMutation);
	const updateFeedType = (updatedFeedType: FeedType | string) => {
		if (isLoggedIn) {
			const variables = {
				feedTab: updatedFeedType as string,
			};

			void feedTypeSelectorMutation({
				variables,
				optimisticResponse: {
					__typename: 'Mutation',
					updateUserPreferences: {
						__typename: 'UserPreferences',
						feedTab: updatedFeedType as FeedType,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation

			// Always write to the cache so the user sees the preference even if there is an error
			writeFragmentInterop({
				fragmentId: '$ROOT_QUERY.userPreferences',
				data: {
					...variables, //handles either feedType or feedTab depending on FF
					__typename: 'UserPreferences',
				},
			});
		}
	};

	return {
		loading,
		queryError,
		mutationError,
		feedType,
		updateFeedType,
		hasSelectedFeedTypePreference: Boolean(data?.userPreferences?.feedTab),
	};
};

export const useAnonymousFeedTypeSelector: UseFeedTypeSelector = (
	defaultPreference = FeedType.FOLLOWING,
) => {
	const [feedType, setFeedType] = useState<FeedType | string>(defaultPreference);
	const [hasSelectedFeedTypePreference, setHasSelectedFeedTypePreference] =
		useState<boolean>(false);
	return {
		loading: false,
		feedType,
		updateFeedType: (feedType: FeedType | string) => {
			setFeedType(feedType);
			setHasSelectedFeedTypePreference(true);
		},
		hasSelectedFeedTypePreference,
	};
};

const getFeedType = (feedQueryParam?: FeedType) =>
	feedQueryParam && Object.values(FeedType).includes(feedQueryParam) ? feedQueryParam : undefined;

export const useFeedTypeOverride: UseFeedTypeOverride = () => {
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const feedQueryParam = getSingleParam(getQueryParams(), 'feedType') as FeedType | undefined;
	const [feedTypeOverride, setFeedTypeOverride] = useState<FeedType | undefined>(
		getFeedType(feedQueryParam),
	);

	useEffect(() => {
		setQueryParams({ feedType: undefined }, true);
	}, [setQueryParams]);

	const clearFeedTypeOverride = () => {
		setFeedTypeOverride(undefined);
	};
	return {
		feedTypeOverride,
		clearFeedTypeOverride,
	};
};

export { FeedType } from './__types__/FeedTypeSelectorMutation.ts';
